import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Layout from '../../components/layoutMasterConfig/layoutMaster';
import taxiIcon from '../../components/dashboard/img/favicon/pet-taxi.png';
import submasterIcon from '../../components/dashboard/img/favicon/franquia.png';
import clientIcon from '../../components/dashboard/img/favicon/garoto.png';
import notificationIcon from '../../components/dashboard/img/favicon/sereia.png';
import runningIcon from '../../components/dashboard/img/favicon/running-track.gif';
import finishedIcon from '../../components/dashboard/img/favicon/finish-line.gif';
import canceledIcon from '../../components/dashboard/img/favicon/location-pin.gif';
import { apiClientMater } from '../../services/setupClientMaster/apiClientMaster';

interface ActivationPost {
  id: number;
  name: string;
  email: string;
  cpf?: string;
  city?: string;
  number?: string;
  car_model?: string;
}

function HomeMasterPage() {
  const [companyActivations, setCompanyActivations] = useState<ActivationPost[]>([]);
  const [driverActivations, setDriverActivations] = useState<ActivationPost[]>([]);
  const [driverCount, setDriverCount] = useState(0);
  const [submasterCount, setSubmasterCount] = useState(0);
  const [clientCount, setClientCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'company' | 'driver'>('company');

  useEffect(() => {
    const apiClient = apiClientMater();

    async function loadDashboard() {
      try {
        const [activationResponse, driversResponse, companiesResponse, clientsResponse] =
          await Promise.all([
            apiClient.post('/getActivationData'),
            apiClient.post('/crud/User'),
            apiClient.post('/crud/Submaster'),
            apiClient.post('/crud/ClientSubmaster'),
          ]);

        setCompanyActivations(activationResponse.data?.[0]?.submaster || []);
        setDriverActivations(activationResponse.data?.[1]?.usuario || []);
        setDriverCount(driversResponse.data?.length || 0);
        setSubmasterCount(companiesResponse.data?.length || 0);
        setClientCount(clientsResponse.data?.length || 0);
      } catch (error) {
        toast.error('Não foi possível carregar o painel master.');
      }
    }

    loadDashboard();
  }, []);

  async function handleEnableUser(id: number, isSubmaster: boolean) {
    try {
      const apiClient = apiClientMater();
      await apiClient.post('/enableUser', { id, isSubmaster });
      toast.success('Ativado com sucesso.');

      if (isSubmaster) {
        setCompanyActivations((previous) =>
          previous.filter((item) => item.id !== id)
        );
      } else {
        setDriverActivations((previous) =>
          previous.filter((item) => item.id !== id)
        );
      }
    } catch (error) {
      toast.error('Existe um erro ao ativar o cadastro.');
    }
  }

  const overviewMetrics = useMemo(
    () => [
      {
        icon: taxiIcon,
        eyebrow: 'Motoristas',
        value: driverCount,
        caption: 'Cadastros operacionais disponíveis',
      },
      {
        icon: submasterIcon,
        eyebrow: 'Empresas',
        value: submasterCount,
        caption: 'Organizações vinculadas à plataforma',
      },
      {
        icon: clientIcon,
        eyebrow: 'Clientes',
        value: clientCount,
        caption: 'Base total de clientes cadastrados',
      },
      {
        icon: notificationIcon,
        eyebrow: 'Alertas',
        value: companyActivations.length + driverActivations.length,
        caption: 'Solicitações aguardando aprovação',
      },
    ],
    [clientCount, companyActivations.length, driverActivations.length, driverCount, submasterCount]
  );

  const operationMetrics = [
    {
      icon: runningIcon,
      eyebrow: 'Em andamento',
      value: '36',
      caption: 'R$ 3.520,00 movimentados no fluxo atual',
    },
    {
      icon: finishedIcon,
      eyebrow: 'Finalizadas',
      value: '12',
      caption: 'R$ 20,00 consolidados no período',
    },
    {
      icon: canceledIcon,
      eyebrow: 'Canceladas',
      value: '5',
      caption: 'R$ 8,50 impactados por cancelamentos',
    },
  ];

  const chartData = [
    { name: 'Motoristas', value: 2400, secondary: 0 },
    { name: 'Track EPI', value: 0, secondary: 25300 },
  ];

  const activeList =
    activeTab === 'company' ? companyActivations : driverActivations;

  return (
    <Layout>
      <section className="dashboard-hero mb-4">
        <span className="pill-badge">Master dashboard</span>
        <h1 className="dashboard-title">Operação central mais clara e executiva</h1>
        <p className="dashboard-subtitle">
          Visualize o ecossistema, aprove pendências e acompanhe os indicadores
          mais relevantes em uma única tela.
        </p>
      </section>

      <section className="dashboard-grid stats-4 mb-4">
        {overviewMetrics.map((metric) => (
          <article key={metric.eyebrow} className="dashboard-card metric-card">
            <div className="metric-card__icon">
              <img src={metric.icon} alt={metric.eyebrow} />
            </div>
            <div>
              <p className="metric-card__eyebrow">{metric.eyebrow}</p>
              <h2 className="metric-card__value">{metric.value}</h2>
              <p className="metric-card__caption">{metric.caption}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="dashboard-grid stats-3 mb-4">
        {operationMetrics.map((metric) => (
          <article key={metric.eyebrow} className="dashboard-card metric-card soft">
            <div className="metric-card__icon">
              <img src={metric.icon} alt={metric.eyebrow} />
            </div>
            <div>
              <p className="metric-card__eyebrow">{metric.eyebrow}</p>
              <h2 className="metric-card__value">{metric.value}</h2>
              <p className="metric-card__caption">{metric.caption}</p>
            </div>
          </article>
        ))}
      </section>

      <div className="row">
        <div className="col-xl-6 mb-4">
          <section className="section-panel h-100">
            <div className="section-heading">
              <div>
                <h3>Ativações pendentes</h3>
                <p>
                  Aprove empresas e motoristas com uma triagem mais direta.
                </p>
              </div>
              <span className="pill-badge">{activeList.length} pendente(s)</span>
            </div>

            <div className="d-flex gap-2 mb-4">
              <button
                type="button"
                className={`btn ${
                  activeTab === 'company' ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => setActiveTab('company')}
              >
                Empresas
              </button>
              <button
                type="button"
                className={`btn ${
                  activeTab === 'driver' ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => setActiveTab('driver')}
              >
                Motoristas
              </button>
            </div>

            <div className="status-list">
              {activeList.length === 0 && (
                <div className="status-item">
                  <div className="status-item__meta">
                    <h4>Nenhuma pendência no momento</h4>
                    <p>A fila de aprovações desta aba está vazia.</p>
                  </div>
                </div>
              )}

              {activeList.map((post) => (
                <div key={post.id} className="status-item">
                  <div className="status-item__meta">
                    <h4>{post.name}</h4>
                    <p>
                      {post.email}
                      {post.cpf ? ` • CPF ${post.cpf}` : ''}
                      {post.car_model ? ` • ${post.car_model}` : ''}
                    </p>
                    <p>
                      {post.city || 'Cidade não informada'}
                      {post.number ? ` • ${post.number}` : ''}
                    </p>
                  </div>

                  <div className="status-item__actions">
                    <span className="pill-badge">Pendente</span>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() =>
                        handleEnableUser(post.id, activeTab === 'company')
                      }
                    >
                      Ativar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="col-xl-6 mb-4">
          <section className="section-panel h-100">
            <div className="section-heading">
              <div>
                <h3>Comissão consolidada</h3>
                <p>Leitura visual comparando operação e receita administrativa.</p>
              </div>
              <span className="pill-badge">Resumo executivo</span>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(18,49,58,0.12)" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  name="Comissão de motoristas"
                  dataKey="value"
                  fill="#0d6efd"
                  radius={[10, 10, 0, 0]}
                />
                <Bar
                  name="Comissão da plataforma"
                  dataKey="secondary"
                  fill="#4f8cff"
                  radius={[10, 10, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </section>
        </div>
      </div>
    </Layout>
  );
}

export default HomeMasterPage;
